import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PrimaryButton,
  SecondaryButton,
} from "@/components/Buttons.jsx";
import { addToast } from "@/store/app/action.js";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";
import Badge from "@/components/Badges.jsx";
import { manageAPI } from "@/api/index.js";

// Mock data structure for fraud management settings
const defaultFraudSettings = {
  thresholds: {
    green: 20,    // < 20 = green (low risk)
    orange: 75,   // < 40 = orange (medium risk)
  },
  orderActions: {
    holdOrangeForReview: false,    // Hold orange orders for manual review
    holdRedForReview: false,        // Hold red orders for manual review
    autoRejectRed: false,          // Automatically reject red orders
  },
  criteria: {
    voipPhone: {
      enabled: true,
      score: 15,
      description: "Phone number is a VoIP number"
    },
    differentStatePhone: {
      enabled: true,
      score: 8,
      description: "Phone number area code from different state"
    },
    foreignPhone: {
      enabled: true,
      score: 25,
      description: "Foreign phone number"
    },
    billingDeliveryDistance: {
      enabled: true,
      score: 12,
      description: "Billing address 25+ miles from delivery address (delivery orders)"
    },
    billingPickupDistance: {
      enabled: true,
      score: 8,
      description: "Billing address 75+ miles from store (pickup orders)"
    },
    highFirstOrder: {
      enabled: true,
      score: 20,
      threshold: 100, // Configurable dollar amount
      description: "First order amount exceeds threshold"
    },
    similarFraudName: {
      enabled: true,
      score: 30,
      description: "Name similar to known fraud user"
    },
    emailAlias: {
      enabled: true,
      score: 10,
      description: "Email contains alias (e.g., user+1@gmail.com)"
    },
    // vpnUsage: {
    //   enabled: true,
    //   score: 18,
    //   description: "Using VPN while placing order"
    // },
    multipleFailedPayments: {
      enabled: true,
      score: 25,
      description: "More than 10% of orders have failed payments"
    },
    rapidOrderPlacement: {
      enabled: true,
      score: 15,
      description: "Multiple orders placed within 24 hours"
    },
    suspiciousDeviceFingerprint: {
      enabled: true,
      score: 20,
      description: "Device fingerprint matches known fraud patterns"
    },
    disposableEmail: {
      enabled: true,
      score: 28,
      description: "Using disposable/temporary email service (e.g., 10minutemail.com)"
    },
    deliveryBillingMismatch: {
      enabled: true,
      score: 12,
      description: "Delivery address doesn't match billing address"
    }
  }
};

function FraudManagementPanel() {
  const dispatch = useDispatch();
  const { selectedLocation, selectedMerchant } = useSelector((state) => state.app);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fraudSettings, setFraudSettings] = useState(defaultFraudSettings);
  const [isDirty, setIsDirty] = useState(false);
  const [applyToAllLocations, setApplyToAllLocations] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Helper function to get criterion description by ID
  const getCriterionDescription = (criterionId) => {
    return defaultFraudSettings.criteria[criterionId]?.description || criterionId;
  };

  // Map API keys to internal keys
  const mapApiKeyToInternalKey = (apiKey) => {
    const keyMap = {
      'voip_phone': 'voipPhone',
      'different_state_phone': 'differentStatePhone',
      'foreign_phone': 'foreignPhone',
      'billing_delivery_distance': 'billingDeliveryDistance',
      'billing_pickup_distance': 'billingPickupDistance',
      'high_first_order': 'highFirstOrder',
      'similar_fraud_name': 'similarFraudName',
      'email_alias': 'emailAlias',
      'vpn_usage': 'vpnUsage',
      'multiple_failed_payments': 'multipleFailedPayments',
      'rapid_order_placement': 'rapidOrderPlacement',
      'suspicious_device_fingerprint': 'suspiciousDeviceFingerprint',
      'disposable_email': 'disposableEmail',
      'delivery_billing_mismatch': 'deliveryBillingMismatch'
    };
    return keyMap[apiKey] || apiKey;
  };

  // Map internal keys to API keys
  const mapInternalKeyToApiKey = (internalKey) => {
    const keyMap = {
      'voipPhone': 'voip_phone',
      'differentStatePhone': 'different_state_phone',
      'foreignPhone': 'foreign_phone',
      'billingDeliveryDistance': 'billing_delivery_distance',
      'billingPickupDistance': 'billing_pickup_distance',
      'highFirstOrder': 'high_first_order',
      'similarFraudName': 'similar_fraud_name',
      'emailAlias': 'email_alias',
      'vpnUsage': 'vpn_usage',
      'multipleFailedPayments': 'multiple_failed_payments',
      'rapidOrderPlacement': 'rapid_order_placement',
      'suspiciousDeviceFingerprint': 'suspicious_device_fingerprint',
      'disposableEmail': 'disposable_email',
      'deliveryBillingMismatch': 'delivery_billing_mismatch'
    };
    return keyMap[internalKey] || internalKey;
  };

  // Load fraud settings from API
  const loadFraudSettings = async () => {
    setLoading(true);
    try {
      const response = await manageAPI.get("fraud-settings");
      const data = response.data.data; // Extract data from response wrapper
      
      // Transform API response to match our state structure
      const transformedSettings = {
        thresholds: {
          green: data.fraud_management?.thresholds?.green || 20,
          orange: data.fraud_management?.thresholds?.orange || 40
        },
        orderActions: {
          holdOrangeForReview: data.fraud_management?.order_actions?.hold_orange_for_review || false,
          holdRedForReview: data.fraud_management?.order_actions?.hold_red_for_review || false,
          autoRejectRed: data.fraud_management?.order_actions?.auto_reject_red || false
        },
        criteria: {}
      };
      
      // Transform criteria object to match our structure
      if (data.fraud_management?.criteria) {
        Object.entries(data.fraud_management.criteria).forEach(([key, criterion]) => {
          // Map API keys to our internal keys
          const mappedKey = mapApiKeyToInternalKey(key);
          transformedSettings.criteria[mappedKey] = {
            enabled: criterion.enabled,
            score: criterion.score,
            threshold: criterion.threshold,
            description: getCriterionDescription(mappedKey)
          };
        });
      }
      
      setFraudSettings(transformedSettings);
    } catch (error) {
      console.error('Error loading fraud settings:', error);
      dispatch(addToast({
        type: "error",
        message: "Failed to load fraud settings",
        timer: 5
      }));
      // Fall back to default settings
      setFraudSettings(defaultFraudSettings);
    } finally {
      setLoading(false);
    }
  };

  // Save fraud settings to API
  const saveFraudSettings = async () => {
    setSaving(true);
    try {
      // Generate payload matching API structure
      const payload = {
        apply_to_all_locations: applyToAllLocations,
        fraud_management: {
          thresholds: {
            green: fraudSettings.thresholds.green,
            orange: fraudSettings.thresholds.orange
          },
          order_actions: {
            hold_orange_for_review: fraudSettings.orderActions.holdOrangeForReview,
            hold_red_for_review: fraudSettings.orderActions.holdRedForReview,
            auto_reject_red: fraudSettings.orderActions.autoRejectRed
          },
          criteria: {}
        }
      };

      // Transform criteria to API format
      Object.entries(fraudSettings.criteria).forEach(([key, criteria]) => {
        const apiKey = mapInternalKeyToApiKey(key);
        payload.fraud_management.criteria[apiKey] = {
          enabled: criteria.enabled,
          score: criteria.score
        };
        if (criteria.threshold) {
          payload.fraud_management.criteria[apiKey].threshold = criteria.threshold;
        }
      });

      
      await manageAPI.put("fraud-settings", payload);
      
      dispatch(addToast({
        type: "success",
        message: "Fraud management settings saved successfully",
        timer: 5
      }));
      setIsDirty(false);
    } catch (error) {
      console.error('Error saving fraud settings:', error);
      dispatch(addToast({
        type: "error",
        message: "Failed to save fraud settings",
        timer: 5
      }));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadFraudSettings();
  }, [selectedLocation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if orange threshold is less than 70% of max score
    const maxScore = calculateMaxScore();
    const orangeThresholdPercent = (fraudSettings.thresholds.orange / maxScore) * 100;
    
    if (orangeThresholdPercent < 70) {
      setShowConfirmationModal(true);
    } else {
      await saveFraudSettings();
    }
  };

  const handleConfirmSave = async () => {
    setShowConfirmationModal(false);
    await saveFraudSettings();
  };

  const updateThreshold = (level, value) => {
    const maxScore = calculateMaxScore();
    const newValue = Math.max(0, Math.min(maxScore, parseInt(value) || 0));
    setFraudSettings(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [level]: newValue
      }
    }));
    setIsDirty(true);
  };

  const handleThresholdDrag = (e, level) => {
    e.preventDefault();
    const sliderRect = e.currentTarget.parentElement.parentElement.getBoundingClientRect();
    const startX = e.clientX;
    const startValue = fraudSettings.thresholds[level];
    const maxScore = calculateMaxScore();
    
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaPercent = (deltaX / sliderRect.width) * maxScore;
      const newValue = Math.max(0, Math.min(maxScore, Math.round(startValue + deltaPercent)));
      
      // Ensure proper ordering: green < orange
      if (level === 'green' && newValue >= fraudSettings.thresholds.orange) {
        return;
      }
      if (level === 'orange' && newValue <= fraudSettings.thresholds.green) {
        return;
      }
      
      updateThreshold(level, newValue);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const updateCriteria = (criteriaKey, field, value) => {
    setFraudSettings(prev => ({
      ...prev,
      criteria: {
        ...prev.criteria,
        [criteriaKey]: {
          ...prev.criteria[criteriaKey],
          [field]: value
        }
      }
    }));
    setIsDirty(true);
  };

  const updateCriteriaScore = (criteriaKey, value) => {
    const newValue = Math.max(0, Math.min(75, parseInt(value) || 0));
    updateCriteria(criteriaKey, 'score', newValue);
  };

  const updateCriteriaThreshold = (criteriaKey, value) => {
    const newValue = Math.max(0, parseInt(value) || 0);
    updateCriteria(criteriaKey, 'threshold', newValue);
  };

  const getRiskLevel = (score) => {
    if (score < fraudSettings.thresholds.green) return 'green';
    if (score < fraudSettings.thresholds.orange) return 'orange';
    return 'red';
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'green': return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      case 'orange': return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
      case 'red': return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      default: return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const calculateMaxScore = () => {
    return Object.values(fraudSettings.criteria)
      .filter(criteria => criteria.enabled)
      .reduce((sum, criteria) => sum + criteria.score, 0);
  };

  if (loading) {
    return (
      <div className="grow">
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-500">Loading fraud management settings...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grow">
      <form onSubmit={handleSubmit}>
        {/* Panel body */}
        <div className="p-6 space-y-8">
          <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-5">
            Fraud Management Configuration
          </h2>

          {/* Risk Level Thresholds */}
          <section>
            <h3 className="text-xl text-slate-800 dark:text-slate-100 font-semibold mb-4">
              Risk Level Thresholds
            </h3>
            
            {/* Risk Level Slider */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-6">
              <div className="mb-4">
                <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400 mb-2">
                  <span>Low Risk (Green)</span>
                  <span>Medium Risk (Orange)</span>
                  <span>High Risk (Red)</span>
                </div>
                
                {/* Slider Track */}
                <div className="relative h-8 bg-gradient-to-r from-green-200 via-orange-200 to-red-200 dark:from-green-800 dark:via-orange-800 dark:to-red-800 rounded-lg">
                  {/* Green Zone */}
                  <div 
                    className="absolute left-0 top-0 h-full bg-green-300 dark:bg-green-700 rounded-l-lg"
                    style={{ width: `${(fraudSettings.thresholds.green / Math.max(calculateMaxScore(), 100)) * 100}%` }}
                  ></div>
                  
                  {/* Orange Zone */}
                  <div 
                    className="absolute top-0 h-full bg-orange-300 dark:bg-orange-700"
                    style={{ 
                      left: `${(fraudSettings.thresholds.green / Math.max(calculateMaxScore(), 100)) * 100}%`,
                      width: `${((fraudSettings.thresholds.orange - fraudSettings.thresholds.green) / Math.max(calculateMaxScore(), 100)) * 100}%`
                    }}
                  ></div>
                  
                  {/* Red Zone */}
                  <div 
                    className="absolute right-0 top-0 h-full bg-red-300 dark:bg-red-700 rounded-r-lg"
                    style={{ width: `${((Math.max(calculateMaxScore(), 100) - fraudSettings.thresholds.orange) / Math.max(calculateMaxScore(), 100)) * 100}%` }}
                  ></div>
                  
                  {/* Breakpoint Handles */}
                  <div className="absolute inset-0 flex items-center">
                    {/* Green/Orange Breakpoint */}
                    <div 
                      className="absolute w-1 h-full bg-white dark:bg-slate-200 shadow-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-100"
                      style={{ left: `${(fraudSettings.thresholds.green / Math.max(calculateMaxScore(), 100)) * 100}%` }}
                      onMouseDown={(e) => handleThresholdDrag(e, 'green')}
                    >
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-white dark:bg-slate-800 px-2 py-1 rounded text-xs font-medium shadow-lg border dark:border-slate-600">
                        {fraudSettings.thresholds.green}
                      </div>
                    </div>
                    
                    {/* Orange/Red Breakpoint */}
                    <div 
                      className="absolute w-1 h-full bg-white dark:bg-slate-200 shadow-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-100"
                      style={{ left: `${(fraudSettings.thresholds.orange / Math.max(calculateMaxScore(), 100)) * 100}%` }}
                      onMouseDown={(e) => handleThresholdDrag(e, 'orange')}
                    >
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-white dark:bg-slate-800 px-2 py-1 rounded text-xs font-medium shadow-lg border dark:border-slate-600">
                        {fraudSettings.thresholds.orange}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Score Scale */}
                {(() => {
                  const maxScore = Math.max(calculateMaxScore(), 100);
                  const scalePoints = [0, Math.floor(maxScore * 0.25), Math.floor(maxScore * 0.5), Math.floor(maxScore * 0.75), maxScore];
                  
                  return (
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mt-2">
                      {scalePoints.map((point, index) => (
                        <span key={index}>{point}</span>
                      ))}
                    </div>
                  );
                })()}
              </div>
              
              <div className="text-sm text-slate-600 dark:text-slate-400">
                <div className="flex justify-between mb-2">
                  <span><strong>Maximum possible score:</strong> {calculateMaxScore()} points</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-1 rounded text-xs ${getRiskColor('green')}`}>
                    Green: &lt; {fraudSettings.thresholds.green} pts
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${getRiskColor('orange')}`}>
                    Orange: {fraudSettings.thresholds.green}-{fraudSettings.thresholds.orange - 1} pts
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${getRiskColor('red')}`}>
                    Red: ≥ {fraudSettings.thresholds.orange} pts
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Order Actions */}
          <section>
            <h3 className="text-xl text-slate-800 dark:text-slate-100 font-semibold mb-4">
              Order Actions (Delivery Orders Only)
            </h3>
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Note:</strong> Pickup orders will always be flagged for manual review regardless of fraud score. 
                These settings only apply to delivery orders.
              </p>
            </div>
            <div className="space-y-6">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <h4 className="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                  Medium Risk (Orange) Delivery Orders
                </h4>
                <div className="flex items-center space-x-3">
                  <ToggleSwitch
                    checked={fraudSettings.orderActions.holdOrangeForReview}
                    onChange={(checked) => {
                      setFraudSettings(prev => ({
                        ...prev,
                        orderActions: {
                          ...prev.orderActions,
                          holdOrangeForReview: checked,
                          // Auto-enable red hold for review if orange is enabled
                          holdRedForReview: checked ? true : prev.orderActions.holdRedForReview
                        }
                      }));
                      setIsDirty(true);
                    }}
                    name="hold_orange_review"
                    onLabel="Hold for Review"
                    offLabel="Hold for Review"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Hold orange risk delivery orders for manual review
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Delivery orders will be paused and require manual approval before processing
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <h4 className="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
                  High Risk (Red) Delivery Orders
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <ToggleSwitch
                      checked={fraudSettings.orderActions.holdRedForReview}
                      disabled={fraudSettings.orderActions.autoRejectRed}
                      onChange={(checked) => {
                        setFraudSettings(prev => ({
                          ...prev,
                          orderActions: {
                            ...prev.orderActions,
                            holdRedForReview: checked
                          }
                        }));
                        setIsDirty(true);
                      }}
                      name="hold_red_review"
                      onLabel="Hold for Review"
                      offLabel="Hold for Review"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Hold red risk delivery orders for manual review
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {fraudSettings.orderActions.autoRejectRed 
                          ? "Disabled when Auto Reject & Ban is enabled"
                          : "Delivery orders will be paused and require manual approval before processing"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <ToggleSwitch
                      checked={fraudSettings.orderActions.autoRejectRed}
                      onChange={(checked) => {
                        setFraudSettings(prev => ({
                          ...prev,
                          orderActions: {
                            ...prev.orderActions,
                            autoRejectRed: checked,
                            // Auto-disable hold for review when auto reject is enabled
                            // When disabled, sync with orange hold for review setting
                            holdRedForReview: checked ? false : (prev.orderActions.holdOrangeForReview ? true : prev.orderActions.holdRedForReview)
                          }
                        }));
                        setIsDirty(true);
                      }}
                      name="auto_reject_red"
                      onLabel="Auto Reject & Ban"
                      offLabel="Auto Reject & Ban"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Automatically reject red risk delivery orders and ban customer
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Delivery orders will be automatically declined and customer will be banned
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Fraud Criteria */}
          <section>
            <h3 className="text-xl text-slate-800 dark:text-slate-100 font-semibold mb-4">
              Fraud Detection Criteria
            </h3>
            <div className="space-y-6">
              {Object.entries(fraudSettings.criteria).map(([key, criteria]) => (
                <div key={key} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <ToggleSwitch
                          checked={criteria.enabled}
                          onChange={(checked) => updateCriteria(key, 'enabled', checked)}
                          name={`criteria_${key}`}
                          onLabel=""
                          offLabel=""
                        />
                        <h4 className="text-lg font-medium text-slate-800 dark:text-slate-100">
                          {criteria.description}
                        </h4>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-500">Score:</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={criteria.score}
                        onChange={(e) => updateCriteriaScore(key, e.target.value)}
                        className="form-input w-16 text-center"
                        disabled={!criteria.enabled}
                      />
                      <span className="text-sm text-slate-500">pts</span>
                    </div>
                  </div>
                  
                  {/* Special threshold input for high first order */}
                  {key === 'highFirstOrder' && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Order amount threshold:
                        </label>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-500">$</span>
                          <input
                            type="number"
                            min="0"
                            value={criteria.threshold}
                            onChange={(e) => updateCriteriaThreshold(key, e.target.value)}
                            className="form-input w-20 text-center"
                            disabled={!criteria.enabled}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              ))}
            </div>
          </section>

        </div>

        {/* Panel footer */}
        <footer>
          <div className="flex flex-col px-6 py-5 border-t border-slate-200 dark:border-slate-700">
            {selectedMerchant?.locations?.length > 1 && (
              <div className="mb-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="apply_to_all_locations"
                    className="form-checkbox w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    checked={applyToAllLocations}
                    onChange={(e) => setApplyToAllLocations(e.target.checked)}
                  />
                  <label
                    htmlFor="apply_to_all_locations"
                    className="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    Apply these settings to all {selectedMerchant.name} locations
                  </label>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-7">
                  This will overwrite fraud management settings for all locations under {selectedMerchant.name}
                </p>
              </div>
            )}
            <div className="flex self-end space-x-3">
              <SecondaryButton
                type="button"
                onClick={() => {
                  setFraudSettings(defaultFraudSettings);
                  setIsDirty(false);
                }}
                disabled={!isDirty}
              >
                Reset to Default
              </SecondaryButton>
              <PrimaryButton
                type="submit"
                disabled={!isDirty}
                loading={saving}
              >
                Save Changes
              </PrimaryButton>
            </div>
          </div>
        </footer>
      </form>

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                Confirm Low Risk Threshold
              </h3>
              
              <div className="mb-4">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  You are about to set the red threshold (high risk) to less than 70% of the maximum possible fraud score. This will result in a very wide high-risk zone and may flag a large number of orders as high-risk. Are you sure you want to continue?
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <SecondaryButton
                  onClick={() => setShowConfirmationModal(false)}
                  disabled={saving}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  onClick={handleConfirmSave}
                  loading={saving}
                >
                  Continue & Save
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FraudManagementPanel;
